/* eslint-disable react/display-name */
import React, { useMemo } from "react";
import { styled, theme, CSS } from "@puzzle/theme";

const DEFAULT_COLOR = "text-neutral-base";
const DEFAULT_HEADING_COLOR = "text-neutral-highlight";

const Root = styled("span", {});

type Theme = typeof theme;

type FontSizes = keyof Theme["fontSizes"];
type LineHeights = keyof Theme["lineHeights"];
type FontWeights = keyof Theme["fontWeights"];
type LetterSpacings = keyof Theme["letterSpacings"];
type Colors = keyof Theme["colors"];

const HEADING_VARIANTS = [
  "heading1",
  "heading2",
  "heading2h",
  "heading3",
  "headingL",
  "headingM",
  "headingS",
];

type TextProps = React.PropsWithChildren<{
  /** Type maps to the available font sizes. */
  type?: FontSizes;
  /** Variant maps to the available font sizes. */
  variant?: FontSizes;
  /** Size maps to the available font sizes. */
  size?: FontSizes;
  /** Weight maps to the available font weights. */
  weight?: FontWeights;
  /** Line height maps to the available line heights in the theme. */
  lineHeight?: LineHeights;
  /** Letter spacing maps to the available letter spacings. */
  letterSpacing?: LetterSpacings;
  /** Color maps to the availablevars.colors. */
  color?: Colors | string;
  /** As maps to the available elements. */
  as?: string | (() => React.ReactNode);
  /** CSS maps to the available CSS. */
  css?: CSS;
}>;

export const Text = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TextProps
>(
  (
    {
      type,
      variant = type,
      color = DEFAULT_COLOR,
      size,
      lineHeight,
      letterSpacing,
      weight,
      css: _css,
      ...props
    },
    ref
  ) => {
    const css: CSS = useMemo(() => {
      const result = { ..._css };

      if (color in theme.colors) {
        result.color = `$${color}`;
      } else {
        result.color = color;
      }

      if (variant && variant in theme.fontSizes) {
        result.textStyle = `$${variant}`;

        if (HEADING_VARIANTS.includes(variant) && color === DEFAULT_COLOR) {
          result.color = `$${DEFAULT_HEADING_COLOR}`;
        }
      }

      if (size && size in theme.fontSizes) {
        result.fontSize = `$${size}`;
      }
      if (lineHeight && lineHeight in theme.lineHeights) {
        result.lineHeight = `$${lineHeight}`;
      }
      if (letterSpacing && letterSpacing in theme.letterSpacings) {
        result.letterSpacing = `$${letterSpacing}`;
      }
      if (weight && weight in theme.fontWeights) {
        result.fontWeight = `$${weight}`;
      }

      return result;
    }, [_css, color, letterSpacing, lineHeight, size, variant, weight]);

    return <Root {...props} css={css} ref={ref} />;
  }
);

Text.toString = Root.toString;
